import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { Alert, Button, Form, FormGroup, Input, Label } from 'reactstrap';
import axios from 'axios';
import withRedirect from '../../hoc/withRedirect';
import apiBase, { API_BASE_CANDIDATES } from '../../apiConfig';
import AppContext from '../../context/Context';
import { persistAuthSession } from '../../helpers/utils';

const LoginForm = ({ setRedirect, setRedirectUrl, hasLabel, layout }) => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { setUser, setToken, setSessionReady } = useContext(AppContext);

  const endpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/p-admin/auth.php?action=login`),
    []
  );

  const isDisabled = !login.trim() || !password;

  const loginRequest = async payload => {
    let lastError = null;

    for (const endpoint of endpointCandidates) {
      try {
        return await axios.post(endpoint, payload, {
          timeout: 12000,
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        lastError = error;

        const status = error.response?.status || 0;
        if (status && ![404, 502, 503, 504].includes(status)) {
          throw error;
        }
      }
    }

    throw lastError;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const response = await loginRequest({
        username: login.trim(),
        password,
        remember
      });

      if (response.data.success) {
        toast.success(`Xush kelibsiz, ${response.data.user.full_name}`);
        persistAuthSession({
          token: response.data.token || null,
          user: response.data.user || null
        });
        setToken(response.data.token || null);
        setUser(response.data.user || null);
        setSessionReady(true);

        if (typeof window !== 'undefined') {
          window.location.assign('/dashboard');
          return;
        }

        setRedirectUrl('/dashboard');
        setRedirect(true);
        return;
      }

      const message = response.data.message || 'Login yoki parol noto‘g‘ri';
      setErrorMessage(message);
      toast.error(message);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        (error.response && typeof error.response.data === 'string' && error.response.data) ||
        `API bilan ulanishda xato. Tekshirildi: ${apiBase}`;

      console.error('Login error:', error);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form className="psi-login-form" onSubmit={handleSubmit}>
      {errorMessage ? (
        <Alert color="danger" className="psi-login-alert">
          {errorMessage}
        </Alert>
      ) : null}

      <FormGroup className="mb-3">
        {hasLabel ? <Label className="psi-login-label">Login</Label> : null}
        <div className="psi-login-field">
          <div className="psi-login-field__icon">
            <FontAwesomeIcon icon="user" />
          </div>
          <Input
            className="psi-login-input psi-login-input--framed"
            placeholder={!hasLabel ? 'Login' : 'Loginni kiriting'}
            value={login}
            onChange={({ target }) => setLogin(target.value)}
            autoComplete="username"
            type="text"
          />
        </div>
      </FormGroup>

      <FormGroup className="mb-3">
        {hasLabel ? <Label className="psi-login-label">Parol</Label> : null}
        <div className="psi-login-field">
          <div className="psi-login-field__icon">
            <FontAwesomeIcon icon="lock" />
          </div>
          <Input
            className="psi-login-input psi-login-input--framed psi-login-input--password"
            placeholder={!hasLabel ? 'Parol' : 'Parolni kiriting'}
            value={password}
            onChange={({ target }) => setPassword(target.value)}
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
          />
          <button
            type="button"
            className="psi-login-toggle"
            onClick={() => setShowPassword(prev => !prev)}
            aria-label={showPassword ? 'Parolni yashirish' : 'Parolni ko`rsatish'}
            title={showPassword ? 'Parolni yashirish' : 'Parolni ko`rsatish'}
          >
            <FontAwesomeIcon icon={showPassword ? 'eye-slash' : 'eye'} />
          </button>
        </div>
      </FormGroup>

      <div className="psi-login-row">
        <label className="psi-login-check">
          <input checked={remember} onChange={({ target }) => setRemember(target.checked)} type="checkbox" />
          <span>Eslab qolish</span>
        </label>
        <span className="psi-login-status">
          <FontAwesomeIcon icon="check-circle" />
          Himoyalangan sessiya
        </span>
      </div>

      <FormGroup className="mb-0">
        <Button color="primary" block className="psi-login-submit" disabled={isDisabled || isSubmitting}>
          {isSubmitting ? (
            'Tekshirilmoqda...'
          ) : (
            <>
              <FontAwesomeIcon icon="sign-in-alt" className="mr-2" />
              Kirish
            </>
          )}
        </Button>
      </FormGroup>
    </Form>
  );
};

LoginForm.propTypes = {
  setRedirect: PropTypes.func.isRequired,
  setRedirectUrl: PropTypes.func.isRequired,
  layout: PropTypes.string,
  hasLabel: PropTypes.bool
};

LoginForm.defaultProps = {
  layout: 'basic',
  hasLabel: false
};

export default withRedirect(LoginForm);
